import { CheaterGame, ScanResponse } from "../types";
import { resultCategory, resultLabel } from "../resultCategory";

interface ResultsDisplayProps {
  results: ScanResponse | null;
  error: string | null;
}

interface FlatGame extends CheaterGame {
  cheater: string;
}

/** Flatten {cheater: [games]} into a single list sorted by date desc. */
function flattenAndSort(results: ScanResponse): FlatGame[] {
  return Object.entries(results.cheaters)
    .flatMap(([cheater, games]) => games.map((g) => ({ ...g, cheater })))
    .sort((a, b) => b.date.localeCompare(a.date));
}

export default function ResultsDisplay({ results, error }: ResultsDisplayProps) {
  if (error) {
    return <div className="error">{error}</div>;
  }

  if (!results) {
    return null;
  }

  return (
    <div className="results">
      {results.warnings && results.warnings.length > 0 && (
        <div className="results-warning">
          {results.warnings.map((w, i) => (
            <div key={i}>{w}</div>
          ))}
        </div>
      )}

      {results.total_unique_cheaters === 0 ? (
        <div className="no-results">No cheaters found.</div>
      ) : (
        <>
          <div className="results-summary">
            Found <span>{results.total_unique_cheaters}</span> cheater
            {results.total_unique_cheaters !== 1 && "s"} across{" "}
            <span>{results.total_games_vs_cheaters}</span> game
            {results.total_games_vs_cheaters !== 1 && "s"} over{" "}
            <span>{results.months_back}</span> month{results.months_back !== 1 && "s"}
          </div>

          <p className="results-disclaimer">
            Please note that these players were banned for fair play violations after your
            game(s), not necessarily for cheating against you.
          </p>

          <ul className="games-list">
            {flattenAndSort(results).map((game) => (
              <li key={game.url} className="game-row">
                <span className={`game-result game-result--${resultCategory(game.result)}`}>
                  {resultLabel(game.result)}
                </span>
                <span className="game-date">{game.date}</span>
                <span className="game-cheater">{game.cheater}</span>
                <a
                  href={game.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="game-link"
                  aria-label={`Open game against ${game.cheater} on ${game.date}`}
                >
                  View game →
                </a>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
